import { getGlobal } from '../global';

import type { ApiOldLangPack, ApiOldLangString } from '../api/types';
import type { LangCode, TimeFormat } from '../types';

import { selectSharedSettings } from '../global/selectors/sharedState';
import { createCallbackManager } from './callbacks';
import { formatInteger } from './textFormat';

export interface LangFn {
  (key: string, value?: any, format?: 'i', pluralValue?: number): string;

  isRtl?: boolean;
  code?: LangCode;
  langName?: string;
  timeFormat?: TimeFormat;
}

const SUBSTITUTION_REGEX = /%\d?\$?[sdf@]/g;
const PLURAL_OPTIONS = ['value', 'zeroValue', 'oneValue', 'twoValue', 'fewValue', 'manyValue', 'otherValue'] as const;
// Some rules edited from https://github.com/eemeli/make-plural/blob/master/packages/plurals/cardinals.js
const PLURAL_RULES = {
  /* eslint-disable max-len */
  en: (n: number) => (n !== 1 ? 6 : 2),
  ar: (n: number) => (n === 0 ? 1 : n === 1 ? 2 : n === 2 ? 3 : n % 100 >= 3 && n % 100 <= 10 ? 4 : n % 100 >= 11 ? 5 : 6),
  be: (n: number) => {
    const s = String(n).split('.');
    const t0 = Number(s[0]) === n;
    const n10 = t0 ? Number(s[0].slice(-1)) : 0;
    const n100 = t0 ? Number(s[0].slice(-2)) : 0;
    return n10 === 1 && n100 !== 11 ? 2
      : (n10 >= 2 && n10 <= 4) && (n100 < 12 || n100 > 14) ? 4
        : (t0 && n10 === 0) || (n10 >= 5 && n10 <= 9) || (n100 >= 11 && n100 <= 14) ? 5
          : 6;
  },
  ca: (n: number) => (n !== 1 ? 6 : 2),
  cs: (n: number) => {
    const s = String(n).split('.');
    const i = Number(s[0]);
    const v0 = !s[1];
    return n === 1 && v0 ? 2 : (i >= 2 && i <= 4) && v0 ? 4 : !v0 ? 5 : 6;
  },
  de: (n: number) => (n !== 1 ? 6 : 2),
  es: (n: number) => (n !== 1 ? 6 : 2),
  fa: (n: number) => (n > 1 ? 6 : 2),
  fi: (n: number) => (n !== 1 ? 6 : 2),
  fr: (n: number) => (n > 1 ? 6 : 2),
  id: () => 0,
  it: (n: number) => (n !== 1 ? 6 : 2),
  hr: (n: number) => {
    const s = String(n).split('.');
    const i = s[0];
    const f = s[1] || '';
    const v0 = !s[1];
    const i10 = Number(i.slice(-1));
    const i100 = Number(i.slice(-2));
    const f10 = Number(f.slice(-1));
    const f100 = Number(f.slice(-2));
    return (v0 && i10 === 1 && i100 !== 11) || (f10 === 1 && f100 !== 11) ? 2
      : (v0 && (i10 >= 2 && i10 <= 4) && (i100 < 12 || i100 > 14)) || ((f10 >= 2 && f10 <= 4) && (f100 < 12 || f100 > 14)) ? 4
        : 6;
  },
  hu: (n: number) => (n > 1 ? 6 : 2),
  ko: () => 0,
  ms: () => 0,
  nb: (n: number) => (n > 1 ? 6 : 2),
  nl: (n: number) => (n !== 1 ? 6 : 2),
  pl: (n: number) => (n === 1 ? 2 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 4 : 5),
  'pt-br': (n: number) => (n > 1 ? 6 : 2),
  ru: (n: number) => (n % 10 === 1 && n % 100 !== 11 ? 2 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 4 : 5),
  sk: (n: number) => {
    const s = String(n).split('.');
    const i = Number(s[0]);
    const v0 = !s[1];
    return n === 1 && v0 ? 2 : (i >= 2 && i <= 4) && v0 ? 4 : !v0 ? 5 : 6;
  },
  sr: (n: number) => {
    const s = String(n).split('.');
    const i = s[0];
    const f = s[1] || '';
    const v0 = !s[1];
    const i10 = Number(i.slice(-1));
    const i100 = Number(i.slice(-2));
    const f10 = Number(f.slice(-1));
    const f100 = Number(f.slice(-2));
    return (v0 && i10 === 1 && i100 !== 11) || (f10 === 1 && f100 !== 11) ? 2
      : (v0 && (i10 >= 2 && i10 <= 4) && (i100 < 12 || i100 > 14)) || ((f10 >= 2 && f10 <= 4) && (f100 < 12 || f100 > 14)) ? 4
        : 6;
  },
  tr: (n: number) => (n > 1 ? 6 : 2),
  uk: (n: number) => (n % 10 === 1 && n % 100 !== 11 ? 2 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 4 : 5),
  uz: (n: number) => (n > 1 ? 6 : 2),
  'zh-CN': () => 0,
  zh: () => 0,
  /* eslint-enable max-len */
};

const cache = new Map<string, string>();

let langPack: ApiOldLangPack | undefined;

// 多语言 fallback 包：根据当前用户语言选用。
// - 中文用户：zhFallbackPack（原 fallbackLangPack.ts）优先
// - 葡语用户：ptFallbackPack → enFallbackPack（绝不回退中文）
// - 其他语言：enFallbackPack
let zhFallbackPack: ApiOldLangPack | undefined;
let enFallbackPack: ApiOldLangPack | undefined;
let ptFallbackPack: ApiOldLangPack | undefined;

function normalizeLangCode(langCode: string) {
  const normalized = langCode.trim().replace('_', '-');
  const lowerCased = normalized.toLowerCase();

  if (lowerCased === 'zh-cn' || lowerCased === 'zh-hans') {
    return 'zh-CN' as LangCode;
  }

  if (lowerCased === 'zh-tw' || lowerCased === 'zh-hant') {
    return 'zh-TW' as LangCode;
  }

  if (lowerCased === 'pt' || lowerCased === 'pt-br' || lowerCased === 'pt-pt') {
    return 'pt-br' as LangCode;
  }

  return normalized as LangCode;
}

const {
  addCallback,
  removeCallback,
  runCallbacks,
} = createCallbackManager();

export { addCallback, removeCallback };

let currentLangCode: string | undefined;
let currentTimeFormat: TimeFormat | undefined;

function isCurrentLangChinese(): boolean {
  return Boolean(currentLangCode?.toLowerCase().startsWith('zh'));
}

function isCurrentLangPortuguese(): boolean {
  return Boolean(currentLangCode?.toLowerCase().startsWith('pt'));
}

/**
 * 返回当前语言该查的 fallback 链。
 * 顺序即优先级，前面查不到才查后面。
 */
function getFallbackChain(): Array<ApiOldLangPack | undefined> {
  if (isCurrentLangChinese()) {
    return [zhFallbackPack];
  }
  if (isCurrentLangPortuguese()) {
    return [ptFallbackPack, enFallbackPack];
  }
  return [enFallbackPack];
}

function lookupInFallback(key: string): ApiOldLangString | undefined {
  for (const pack of getFallbackChain()) {
    const value = pack?.[key];
    if (value) return value;
  }
  return undefined;
}

function isFallbackChainLoaded(): boolean {
  if (isCurrentLangChinese()) return Boolean(zhFallbackPack);
  if (isCurrentLangPortuguese()) return Boolean(ptFallbackPack) && Boolean(enFallbackPack);
  return Boolean(enFallbackPack);
}

function createLangFn() {
  return (key: string, value?: any, format?: 'i', pluralValue?: number) => {
    if (value !== undefined) {
      const cacheValue = Array.isArray(value) ? JSON.stringify(value) : value;
      const cached = cache.get(`${key}_${cacheValue}_${format}${pluralValue ? `_${pluralValue}` : ''}`);
      if (cached) {
        return cached;
      }
    }

    if (!isFallbackChainLoaded()) {
      void importFallbackLangPack();
    }

    // 中文用户：本地 fallback 优先（原本行为）。
    // 非中文用户：服务端 langPack 优先，缺失时走对应语言 fallback 链（葡语: pt -> en）。
    // 关键：葡语用户绝不会回退到中文。
    const shouldPreferLocalPack = isCurrentLangChinese();
    const langString = shouldPreferLocalPack
      ? zhFallbackPack?.[key] || langPack?.[key]
      : langPack?.[key] || lookupInFallback(key);
    if (!langString) {
      return key;
    }

    return processTranslation(langString, key, value, format, pluralValue);
  };
}

let translationFn: LangFn = createLangFn();

/**
 * @deprecated Do not translate inside non-reactive contexts. Pass parameters to the component instead
 */
export function oldTranslate(...args: Parameters<LangFn>) {
  return translationFn(...args);
}

export function getTranslationFn(): LangFn {
  return translationFn;
}

/**
 * @deprecated Migrate to `changeLanguage` in `util/localization.ts` instead
 */
export async function oldSetLanguage(langCode: LangCode, callback?: NoneToVoidFunction, withFallback = false) {
  const normalizedLangCode = normalizeLangCode(langCode);
  void withFallback;

  if (langPack && normalizedLangCode === currentLangCode) {
    if (callback) {
      callback();
    }

    return;
  }

  currentLangCode = normalizedLangCode;
  await importFallbackLangPack();
  const newLangPack = {} as ApiOldLangPack;

  cache.clear();

  langPack = newLangPack;
  document.documentElement.lang = normalizedLangCode;

  const global = getGlobal();
  const { languages, timeFormat } = selectSharedSettings(global);
  const langInfo = languages?.find((lang) => normalizeLangCode(lang.langCode) === normalizedLangCode);
  translationFn = createLangFn();
  translationFn.isRtl = Boolean(langInfo?.isRtl);
  translationFn.code = normalizedLangCode.replace('-raw', '') as LangCode;
  translationFn.langName = langInfo?.nativeName;
  translationFn.timeFormat = timeFormat;

  if (callback) {
    callback();
  }

  runCallbacks();
}

export function setTimeFormat(timeFormat: TimeFormat) {
  if (timeFormat && timeFormat === currentTimeFormat) {
    return;
  }

  currentTimeFormat = timeFormat;
  translationFn.timeFormat = timeFormat;

  runCallbacks();
}

async function importFallbackLangPack() {
  // 根据当前语言动态加载不同语言的 fallback。
  // 中文用户只需中文 fallback；葡语用户需要葡语 + 英文双重 fallback；其他语言只需英文。
  if (isCurrentLangChinese()) {
    if (!zhFallbackPack) {
      zhFallbackPack = (await import('./fallbackLangPack')).default;
      runCallbacks();
    }
    return;
  }

  if (isCurrentLangPortuguese()) {
    const promises: Promise<unknown>[] = [];
    if (!ptFallbackPack) {
      promises.push((async () => {
        ptFallbackPack = (await import('./fallbackLangPack.pt')).default;
      })());
    }
    if (!enFallbackPack) {
      promises.push((async () => {
        enFallbackPack = (await import('./fallbackLangPack.en')).default;
      })());
    }
    if (promises.length) {
      await Promise.all(promises);
      runCallbacks();
    }
    return;
  }

  // 其他语言（含英文本身）：只需英文 fallback。
  if (!enFallbackPack) {
    enFallbackPack = (await import('./fallbackLangPack.en')).default;
    runCallbacks();
  }
}

function getPluralOption(amount: number) {
  const langCode = currentLangCode || 'en';
  const optionIndex = PLURAL_RULES[langCode as keyof typeof PLURAL_RULES]
    ? PLURAL_RULES[langCode as keyof typeof PLURAL_RULES](amount)
    : 0;

  return PLURAL_OPTIONS[optionIndex];
}

function processTemplate(template: string, value: any) {
  value = Array.isArray(value) ? value : [value];
  const translationSlices = template.split(SUBSTITUTION_REGEX);
  const initialValue = translationSlices.shift();

  return translationSlices.reduce((result, str, index) => {
    return `${result}${String(value[index] ?? '')}${str}`;
  }, initialValue || '');
}

function processTranslation(
  langString: ApiOldLangString | undefined, key: string, value?: any, format?: 'i', pluralValue?: number,
) {
  const preferredPluralOption = typeof value === 'number' || pluralValue !== undefined
    ? getPluralOption(pluralValue ?? value)
    : 'value';
  const legacyLangString = typeof langString === 'object' ? langString as any : undefined;
  const template = typeof langString === 'string'
    ? langString
    : preferredPluralOption === 'value'
      // Support cached older `langString` interface
      ? (legacyLangString
        ? legacyLangString.value || legacyLangString.otherValue || legacyLangString.oneValue
        : langString)
      : legacyLangString
        ? legacyLangString[preferredPluralOption] || legacyLangString.otherValue
          || legacyLangString.value || legacyLangString.oneValue
        : undefined;

  if (!template?.trim()) {
    const parts = key.split('.');

    return parts[parts.length - 1];
  }

  if (value !== undefined) {
    const formattedValue = format === 'i' ? formatInteger(value) : value;
    const result = processTemplate(template, formattedValue);
    const cacheValue = Array.isArray(value) ? JSON.stringify(value) : value;
    cache.set(`${key}_${cacheValue}_${format}${pluralValue ? `_${pluralValue}` : ''}`, result);
    return result;
  }

  return template;
}
